import React, { useEffect, useMemo } from 'react';

import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Plus } from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';

import { getQueryParams } from 'constants/helper';

import { getEmployeeDetails } from 'services/employee';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import FeedbackMultiEmployeeSelect from '../common/EmployeeMultiSelect/FeedbackMultiEmployeeSelect';
import FeedbackEmployeeDropdown from '../common/FeedbackEmployeeDropdown';
import FeedbackVisibilityDropdown from '../common/FeedbackVisibilityDropdown';
import {
  setRequestedGoal,
  setRequestedProviderEmployees,
  setRequestedQuestion,
  setRequestedReceiverEmployeeId,
  setRequestedVisibility,
  useRequestedFeedbackData,
} from '../slice/feedbackSlice';

export const REQUEST_FEEDBACK_CHARACTERS_LIMIT = 4000;
export interface RequestFeedbackBodyProps {
  prefilledReceiverId?: string;
  openSecondaryPanel: () => void;
}

export const RequestFeedbackBody = ({ prefilledReceiverId, openSecondaryPanel }: RequestFeedbackBodyProps) => {
  const dispatch = useDispatch();

  const { moduleName } = useOrganisationSettings();
  const { goal, questionText, receiverEmployeeId, visibility, providerEmployees } = useRequestedFeedbackData();

  const characterCount = useMemo(() => questionText?.length ?? 0, [questionText]);

  const charLimitError = useMemo(() => characterCount > REQUEST_FEEDBACK_CHARACTERS_LIMIT, [characterCount]);

  const location = useLocation();

  const { search } = location ?? {};
  const { providerId } = getQueryParams(search);
  const toast = useToast();
  const setProviderEmployees = (employees) => {
    dispatch(setRequestedProviderEmployees({ providerEmployees: employees }));
  };

  useEffect(() => {
    if (providerId) {
      try {
        getEmployeeDetails(providerId).then((employeeDataFromURL) => {
          if (!employeeDataFromURL) {
            toast({ title: 'Unable to fetch receiver Details', status: 'error' });
          }

          const { displayName, email, id, profilePhotoSrc: profilePhoto } = employeeDataFromURL;
          const employeeDataForSelect = { displayName, email, id, isSelected: true, profilePhoto };
          setProviderEmployees([employeeDataForSelect]);
        });
      } catch (error) {
        toast({ title: 'Unable to fetch receiver Details', status: 'error' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  // eslint-disable-next-line no-shadow
  const setReceiverId = (employeeId) => {
    if (employeeId === receiverEmployeeId) {
      return;
    }
    dispatch(
      setRequestedReceiverEmployeeId({
        receiverEmployeeId: employeeId,
      })
    );

    if (employeeId !== prefilledReceiverId) {
      dispatch(
        setRequestedGoal({
          goal: null,
        })
      );
    }
  };

  const onFeedbackVisibilitySelect = (type) => {
    dispatch(
      setRequestedVisibility({
        visibility: type.value,
      })
    );
  };

  // eslint-disable-next-line no-shadow
  const onUpdateQuestion = (questionText) => {
    dispatch(
      setRequestedQuestion({
        questionText,
      })
    );
  };

  return (
    <Box w="100%">
      {/* @ts-expect-error: union too complex */}
      <Flex flexDir="column" w="100%" p="8px 12px">
        {/* @ts-expect-error: union too complex */}
        <Grid alignItems="center" templateColumns="repeat(3, 1fr)" gap={0} p="12px 0px">
          {/* @ts-expect-error: union too complex */}
          <GridItem colSpan={1}>
            <TextLabel color="text.g2">
              Request feedback from
              <span
                style={{
                  color: '#EC3B64',
                }}
              >
                *
              </span>{' '}
              :{' '}
            </TextLabel>
          </GridItem>
          <GridItem colSpan={2}>
            {/* @ts-expect-error 'selectedEmployees' not of type 'IntrinsicAttributes & Props' */}
            <FeedbackMultiEmployeeSelect onSelect={setProviderEmployees} selectedEmployees={providerEmployees} />
          </GridItem>
        </Grid>
        <Grid alignItems="center" templateColumns="repeat(3, 1fr)" gap={0} p="12px 0px">
          <GridItem colSpan={1}>
            <TextLabel color="text.g2">
              Feedback for
              <span
                style={{
                  color: '#EC3B64',
                }}
              >
                *
              </span>{' '}
              :{' '}
            </TextLabel>
          </GridItem>
          <GridItem colSpan={2}>
            <FeedbackEmployeeDropdown onSelect={setReceiverId} selectedEmployeeId={prefilledReceiverId} />
          </GridItem>
        </Grid>
        <Grid alignItems="center" templateColumns="repeat(3, 1fr)" gap={0} p="12px 0px">
          <GridItem colSpan={1}>
            <TextLabel color="text.g2">Visibility : </TextLabel>
          </GridItem>
          <GridItem colSpan={2}>
            <FeedbackVisibilityDropdown
              onChange={onFeedbackVisibilitySelect}
              selectedValue={visibility}
              showLabel={false}
            />
          </GridItem>
        </Grid>
        <Flex mt="8px" flexDir="column">
          {/* @ts-expect-error: union too complex */}
          <AutoResizeInput
            minRows={3}
            isDashed
            value={questionText}
            onChange={(e) => {
              onUpdateQuestion(e.target.value);
            }}
            placeholder="Type details of the feedback you want to request"
          />
          <Flex>
            {charLimitError && (
              <TextBodyMedium color="functional.C62C02" align="left">
                Character limit exceeded, you request will not be sent
              </TextBodyMedium>
            )}
            <TextBodyMedium ml="auto" color={charLimitError ? 'functional.C62C02' : 'text.g3'}>
              {characterCount}/{REQUEST_FEEDBACK_CHARACTERS_LIMIT}
            </TextBodyMedium>
          </Flex>
        </Flex>
      </Flex>
      <Flex alignItems="start" p="8px" flexDir="column" mt="12px">
        {/* @ts-expect-error 'goal' is possibly 'null' */}
        {!goal?.uuid ? (
          <Button
            variant="tertiary"
            StartIcon={Plus}
            onClick={receiverEmployeeId ? openSecondaryPanel : () => {}}
            cursor={receiverEmployeeId ? 'pointer !important' : 'not-allowed !important'}
            opacity={receiverEmployeeId ? 1 : 0.6}
          >
            Add Related {moduleName}
          </Button>
        ) : (
          <Box p="8px 12px ">
            <TextLabel mb="4px" color="text.g2">
              Related {moduleName}
            </TextLabel>
            <Flex alignItems="center" cursor="pointer" onClick={openSecondaryPanel}>
              <KeyResult color="#7651BC" boxSize="18px" mr="8px" />

              {/* @ts-expect-error Property 'title' does not exist on type 'never' */}
              <TextBodyMedium>{goal?.title}</TextBodyMedium>
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  );
};
